import { create } from 'zustand';

import { ClassNoteRepository } from '../../api/repository/class-note';
import type { ClassNoteUpsertPayload } from '../../api/repository/class-note';
import { ClassNoteLessonRepository } from '../../api/repository/class-note-lesson';
import { normalizeError } from '../../api/core/errors';
import type { Id, QltClassNote, QltClassNoteData, QltClassNoteLesson } from '../../api/types';
import { msg, type MaybeMsg } from '../../i18n/msg.ts';
import { userError } from '../userError';
import { needsSend, noticeDigest, noticeFailureMessage, noticeRecord, noticeUnrecordedMessage, parentNoticeOf, parentNoticeRequest } from './after-lesson-reminder';
import type { ParentNotice } from './after-lesson-reminder';

function recordId(value: { id?: Id; _id?: string }): Id | undefined {
  return value.id === undefined ? value._id : value.id;
}

function sameId(a: Id | null | undefined, b: Id | null | undefined): boolean {
  return a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);
}

// merges into note.data and throws, so each caller words its own failure
function writeData(note: QltClassNote, id: Id, changes: Partial<QltClassNoteData>, lesson?: string): Promise<QltClassNote> {
  const data = { ...(note.data || {}), ...changes };
  return ClassNoteRepository.update<QltClassNote>(id, { ...(lesson === undefined ? {} : { lesson }), data });
}

// none: nothing to tell; unchanged: same content already posted; failed: note saved, post refused; unrecorded: posted, marker not saved
export type ParentNoticeOutcome =
  | { kind: 'none' | 'unchanged' | 'sent' }
  | { kind: 'failed' | 'unrecorded'; message: MaybeMsg };

export interface QltClassNoteState {
  /** The class note the board is on; a late response for another one is dropped. */
  classNoteId: Id | null;
  currentNote: QltClassNote | null;
  currentLesson: QltClassNoteLesson | null;
  loading: boolean;
  error: MaybeMsg | null;
  loadNote: (classNoteId: Id) => Promise<QltClassNote | null>;
  upsertForCell: (payload: ClassNoteUpsertPayload) => Promise<QltClassNote | null>;
  loadLesson: (classNoteId: Id) => Promise<QltClassNoteLesson | null>;
  linkLesson: (topicLesson: Id, lessonIndex: string) => Promise<QltClassNoteLesson | null>;
  saveData: (changes: Partial<QltClassNoteData>, lesson?: string) => Promise<QltClassNote | null>;
  notifyParents: (notice: ParentNotice | null) => Promise<ParentNoticeOutcome>;
  selectNote: (note: QltClassNote | null) => void;
  clear: () => void;
}

export const useQltClassNoteStore = create<QltClassNoteState>((set, get) => ({
  classNoteId: null,
  currentNote: null,
  currentLesson: null,
  loading: false,
  error: null,

  async loadNote(classNoteId) {
    // another period's note and lesson never stay on screen while this one loads
    const { currentNote: shown, currentLesson } = get();
    const keep = shown !== null && sameId(recordId(shown), classNoteId);
    set({ classNoteId, currentNote: keep ? shown : null, currentLesson: keep ? currentLesson : null, loading: true, error: null });
    try {
      const currentNote = await ClassNoteRepository.fetchOne<QltClassNote>(classNoteId);
      if (!sameId(get().classNoteId, classNoteId)) return currentNote;
      set({ currentNote, loading: false });
      return currentNote;
    } catch (error) {
      if (sameId(get().classNoteId, classNoteId)) set({ loading: false, error: userError(error) });
      return null;
    }
  },

  async upsertForCell(payload) {
    set({ loading: true, error: null });
    try {
      const currentNote = await ClassNoteRepository.upsert(payload);
      set({ classNoteId: currentNote ? recordId(currentNote) ?? null : null, currentNote, loading: false });
      return currentNote;
    } catch (error) {
      set({ loading: false, error: userError(error) });
      return null;
    }
  },

  async loadLesson(classNoteId) {
    set({ loading: true, error: null });
    try {
      const currentLesson = await ClassNoteLessonRepository.getLessonByClassNote(classNoteId);
      if (!sameId(get().classNoteId, classNoteId)) return currentLesson;
      set({ currentLesson, loading: false });
      return currentLesson;
    } catch (error) {
      if (sameId(get().classNoteId, classNoteId)) set({ loading: false, error: userError(error) });
      return null;
    }
  },

  async linkLesson(topicLesson, lessonIndex) {
    const note = get().currentNote;
    const classNote = note ? recordId(note) : undefined;
    if (classNote === undefined) {
      set({ error: msg('errors:qlt.noClassNote') });
      return null;
    }
    set({ loading: true, error: null });
    try {
      const current = get().currentLesson;
      const currentId = current ? recordId(current) : undefined;
      const data = { classNote, topicLesson, lessonIndex };
      const currentLesson =
        currentId === undefined
          ? await ClassNoteLessonRepository.create<QltClassNoteLesson>(data)
          : await ClassNoteLessonRepository.update<QltClassNoteLesson>(currentId, data);
      set({ currentLesson, loading: false });
      return currentLesson;
    } catch (error) {
      set({ loading: false, error: userError(error) });
      return null;
    }
  },

  async saveData(changes, lesson) {
    const current = get().currentNote;
    const id = current ? recordId(current) : undefined;
    if (!current || id === undefined) {
      set({ error: msg('errors:qlt.noClassNote') });
      return null;
    }
    set({ loading: true, error: null });
    try {
      const updated = await writeData(current, id, changes, lesson);
      if (sameId(get().classNoteId, id)) set({ currentNote: updated, loading: false });
      return updated;
    } catch (error) {
      if (sameId(get().classNoteId, id)) set({ loading: false, error: userError(error) });
      return null;
    }
  },

  // one parent notice per new content, recorded in note.data.parentNotice; call after saveData succeeded
  async notifyParents(notice) {
    if (!notice) return { kind: 'none' };
    const current = get().currentNote;
    const id = current ? recordId(current) : undefined;
    if (!current || id === undefined) return { kind: 'failed', message: msg('errors:qlt.noClassNote') };
    const digest = noticeDigest(notice);
    if (!needsSend(digest, parentNoticeOf(current.data))) return { kind: 'unchanged' };
    set({ loading: true });
    let post: unknown;
    try {
      post = await ClassNoteRepository.parentNotice(id, parentNoticeRequest(notice));
    } catch (error) {
      set({ loading: false });
      return { kind: 'failed', message: noticeFailureMessage(normalizeError(error), userError(error)) };
    }
    try {
      const updated = await writeData(current, id, { parentNotice: noticeRecord(post, notice, digest, new Date()) });
      if (sameId(get().classNoteId, id)) set({ currentNote: updated, loading: false });
      return { kind: 'sent' };
    } catch (error) {
      set({ loading: false });
      return { kind: 'unrecorded', message: noticeUnrecordedMessage() };
    }
  },

  selectNote: (currentNote) => set({ classNoteId: currentNote ? recordId(currentNote) ?? null : null, currentNote, currentLesson: null, error: null }),

  clear: () => set({ classNoteId: null, currentNote: null, currentLesson: null, loading: false, error: null }),
}));
